import {
  Autocomplete,
  ChipProps,
  TextField,
  TextFieldProps,
  Popper,
} from '@mui/material'
import { Brand, RetailerAccount } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

interface RetailerAccountBrandAutocompleteProps {
  brandFilter?: string
  onChange: (value: RetailerAccountBrandAutocompleteBrand[]) => void
  value: (string | number | RetailerAccountBrandAutocompleteBrand)[]
  showAll?: boolean
  slotProps?: {
    textField?: TextFieldProps
    chip?: ChipProps
  }
}

// - we want an "all" option
// - our options are Brands
// - but we don't want to have to fill out a million empty fields to make it a
//   Brand
// So we just use a subset of Brand
export type RetailerAccountBrandAutocompleteBrand = Pick<
  Brand,
  'id' | 'name' | 'slug'
>

const allItem: RetailerAccountBrandAutocompleteBrand = {
  id: '-1',
  name: I18n.t('filters.all'),
  slug: '',
}

const RetailerAccountBrandAutocomplete: React.FC<
  React.PropsWithChildren<RetailerAccountBrandAutocompleteProps>
> = (props) => {
  const { brandFilter } = props
  const currentRetailerAccount: RetailerAccount = useSelector(
    currentRetailerAccountSelector,
  )

  const brands = useMemo(() => {
    const brands: Brand[] = []

    for (const retailer of currentRetailerAccount.retailers || []) {
      if (retailer.brand) {
        brands.push(retailer.brand)
      }
    }

    brands.sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    if (brandFilter === 'industry') {
      return brands.filter((brand) => brand.community)
    } else if (brandFilter === 'brand') {
      return brands.filter((brand) => !brand.community)
    }

    return brands
  }, [currentRetailerAccount.retailers, brandFilter])

  const value = useMemo(() => {
    const value: RetailerAccountBrandAutocompleteBrand[] = []
    for (const stringPairOrBrand of props.value) {
      let foundBrand: Brand | undefined = undefined

      if (typeof stringPairOrBrand === 'number') {
        foundBrand = brands.find(
          (x) => String(x.id) === String(stringPairOrBrand),
        )
      } else if (typeof stringPairOrBrand === 'string') {
        const [field, identifier] = stringPairOrBrand.split(':')

        if (!field) {
          continue
        }
        // Would it be a better idea to have a property for the retailer id here?
        if (field && identifier) {
          if (field === 'id') {
            foundBrand = brands.find((x) => String(x.id) === String(identifier))
          } else if (field === 'slug') {
            foundBrand = brands.find(
              (x) => String(x.slug) === String(identifier),
            )
          }
        } else {
          foundBrand = brands.find((x) => String(x.id) === String(field))
        }
      } else {
        foundBrand = brands.find(
          (x) => String(x.id) === String(stringPairOrBrand.id),
        )
      }

      if (foundBrand) {
        value.push(foundBrand)
      }
    }

    if (props.showAll && !value.length) {
      value.push(allItem)
    }

    return value
  }, [brands, props.value, props.showAll])

  const options = props.showAll ? [allItem, ...brands] : brands

  const getOptionLabel = (option: RetailerAccountBrandAutocompleteBrand) =>
    option.name || ''

  return (
    <Autocomplete
      options={options}
      renderInput={(params) => (
        <TextField {...params} {...props.slotProps?.textField} />
      )}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      // TODO enable this for phase 2.
      // multiple
      // value={value}
      value={value[0] || null}
      renderOption={(props, option, state) => {
        return (
          <MenuItemWithCheckbox
            {...props}
            selected={state.selected}
            data-testid={option.name}
          >
            {getOptionLabel(option)}
          </MenuItemWithCheckbox>
        )
      }}
      onChange={(_event, value) => {
        // TODO re-enable this in phase 2.
        // const hasSelectedAll = value.find((x) => x.id === allItem.id)
        // const nextValue = hasSelectedAll ? [] : value
        // props.onChange(nextValue)

        if (value == null) {
          props.onChange([])
        } else {
          if (value.id === allItem.id) {
            props.onChange([])
          } else {
            props.onChange([value])
          }
        }
      }}
      ChipProps={props.slotProps?.chip}
      PopperComponent={(props) => {
        return (
          <Popper
            {...props}
            style={{
              minWidth: props.style?.width,
              width: 'auto',
              maxWidth: '100%',
            }}
            placement="bottom-start"
          />
        )
      }}
    />
  )
}

export default RetailerAccountBrandAutocomplete
